/* ============================================================
   ДАННЫЕ ПРОЕКТОВ ЦИФРОВОГО ДИАК ЗФ
   Источники:
   - ЦифровойДИАК.xlsx — направления деятельности
   - Положения о департаменте и управлениях
   - Презентация In_УКGenAI_апрельv7.pptx — амбиции и дорожная карта
   - Репозиторий ipk-navigator — проект Входного контроля ИПК
   ============================================================ */

export type ProjectStatus = 'active' | 'pilot' | 'planned' | 'done';
export type AIReadiness = 'high' | 'medium' | 'needs_analysis';

export interface ProjectTask {
  id: string;
  title: string;
  start: string; // ISO date
  end: string;   // ISO date
  status: ProjectStatus;
  dependencies?: string[];
  assignee?: string;
  description?: string;
}

export interface ProjectPassport {
  id: string;
  title: string;
  shortTitle: string;
  icon: string;
  status: ProjectStatus;
  aiReadiness: AIReadiness;
  aiPotential: string; // e.g. ">80%"
  group: 'core' | 'support';
  phase: 1 | 2 | 3; // roadmap phase
  phaseLabel: string;
  description: string;
  goal: string;
  scope: string[];
  stakeholders: string[];
  kpis: { label: string; value: string; unit?: string }[];
  risks: { title: string; level: 'high' | 'medium' | 'low'; mitigation: string }[];
  monthlyPlan?: ProjectTask[]; // only for the first active project
  tags: string[];
  repoUrl?: string;
  startDate: string;
  targetDate: string;
}

// ─── GANTT DATA (main page) ────────────────────────────────────────────────
export interface GanttItem {
  id: string;
  projectId: string;
  title: string;
  start: string;
  end: string;
  status: ProjectStatus;
  phaseId: string;
  /** Фактический старт (опц.). Если пуст, а factEnd задан — берётся плановый start. */
  factStart?: string;
  /** Фактическое окончание (опц.). Минимум, чтобы нарисовать «фактическую» полосу. */
  factEnd?: string;
}

/** Полосы главного ганта дашборда. */
export const GANTT_ITEMS: GanttItem[] = [
  // ── ЭТАП 1: Q2 2026 ──────────────────────────────────────────────────────
  {
    id: 'g1',
    projectId: 'ipk-ovss',
    title: 'Входной контроль и экспертизы ИПК — ОВСС (пилот)',
    start: '2026-01-01',
    end: '2026-06-30',
    status: 'pilot',
    phaseId: 'roadmap-phase-1',
  },
  {
    id: 'g2',
    projectId: 'ipk-repairs',
    title: 'Входной контроль и экспертизы ИПК — ремонты (пилот)',
    start: '2026-01-01',
    end: '2026-06-30',
    status: 'pilot',
    phaseId: 'roadmap-phase-1',
  },
  {
    id: 'g3',
    projectId: 'digital-diak-task-detailing',
    title: 'Детализация задач «Цифровой ДИАК ЗФ», расчет ресурсов и мобилизация команды',
    start: '2026-04-01',
    end: '2026-06-30',
    status: 'active',
    phaseId: 'roadmap-phase-1',
  },
  // ── ЭТАП 2: Q2–Q4 2026 ───────────────────────────────────────────────────
  {
    id: 'g5',
    projectId: 'budget-ovss',
    title: 'Планирование и контроль бюджета ОВСС',
    start: '2026-04-01',
    end: '2026-06-30',
    status: 'planned',
    phaseId: 'roadmap-phase-2',
  },
  {
    id: 'g6',
    projectId: 'project-control-ovss',
    title: 'Проектный контроль и согласование договоров ОВСС',
    start: '2026-04-01',
    end: '2026-06-30',
    status: 'planned',
    phaseId: 'roadmap-phase-2',
  },
  {
    id: 'g7',
    projectId: 'budget-repairs',
    title: 'Планирование и контроль бюджета Ремонты/прочее',
    start: '2026-07-01',
    end: '2026-09-30',
    status: 'planned',
    phaseId: 'roadmap-phase-2',
  },
  {
    id: 'g8',
    projectId: 'project-control-repairs',
    title: 'Проектный контроль и согласование договоров Ремонты',
    start: '2026-07-01',
    end: '2026-09-30',
    status: 'planned',
    phaseId: 'roadmap-phase-2',
  },
  {
    id: 'g9',
    projectId: 'ipk-ks',
    title: 'Входной контроль и экспертизы ИПК — капитальное строительство (КС)',
    start: '2026-10-01',
    end: '2026-12-31',
    status: 'planned',
    phaseId: 'roadmap-phase-2',
  },
  {
    id: 'g19',
    projectId: 'ipk-it',
    title: 'Входной контроль и экспертизы ИПК — ИТ',
    start: '2026-10-01',
    end: '2026-12-31',
    status: 'planned',
    phaseId: 'roadmap-phase-2',
  },
  {
    id: 'g20',
    projectId: 'ipk-other',
    title: 'Входной контроль и экспертизы ИПК — прочие инициативы',
    start: '2026-10-01',
    end: '2026-12-31',
    status: 'planned',
    phaseId: 'roadmap-phase-2',
  },
  {
    id: 'g10',
    projectId: 'ipk-protocols',
    title: 'Написание и выпуск протоколов ИПК',
    start: '2026-10-01',
    end: '2026-12-31',
    status: 'planned',
    phaseId: 'roadmap-phase-2',
  },
  {
    id: 'g11',
    projectId: 'analytics',
    title: 'Подготовка аналитических отчётов (автоматизация)',
    start: '2026-10-01',
    end: '2026-12-31',
    status: 'planned',
    phaseId: 'roadmap-phase-2',
  },
  // ── ЭТАП 3: Q4 2026 – Q2 2027 ────────────────────────────────────────────
  {
    id: 'g12',
    projectId: 'budget-ks-it',
    title: 'Планирование и контроль бюджета КС и ИТ',
    start: '2026-10-01',
    end: '2026-12-31',
    status: 'planned',
    phaseId: 'roadmap-phase-3',
  },
  {
    id: 'g13',
    projectId: 'project-control-ks-it',
    title: 'Проектный контроль и согласование договоров КС и ИТ',
    start: '2027-01-01',
    end: '2027-03-31',
    status: 'planned',
    phaseId: 'roadmap-phase-3',
  },
  {
    id: 'g14',
    projectId: 'post-invest',
    title: 'Постинвестиционный контроль',
    start: '2027-04-01',
    end: '2027-06-30',
    status: 'planned',
    phaseId: 'roadmap-phase-3',
  },
  {
    id: 'g15',
    projectId: 'teo-expertise',
    title: 'Экспертиза ТЭО',
    start: '2027-04-01',
    end: '2027-06-30',
    status: 'planned',
    phaseId: 'roadmap-phase-3',
  },
  {
    id: 'g16',
    projectId: 'kism',
    title: 'Работа с КИСМ (автоматизация)',
    start: '2027-04-01',
    end: '2027-06-30',
    status: 'planned',
    phaseId: 'roadmap-phase-3',
  },
  {
    id: 'g17',
    projectId: 'hr-docs',
    title: 'Кадровый документооборот',
    start: '2027-04-01',
    end: '2027-06-30',
    status: 'planned',
    phaseId: 'roadmap-phase-3',
  },
  {
    id: 'g18',
    projectId: 'admin',
    title: 'Административно-хозяйственные вопросы',
    start: '2027-04-01',
    end: '2027-06-30',
    status: 'planned',
    phaseId: 'roadmap-phase-3',
  },
];

// ─── PROJECT PASSPORTS ─────────────────────────────────────────────────────

export const PROJECTS: ProjectPassport[] = [
  // ══════════════════════════════════════════════════════════════════════════
  // 1a. ВХОДНОЙ КОНТРОЛЬ И ЭКСПЕРТИЗЫ ИПК — ОВСС (пилот Этапа 1)
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'ipk-ovss',
    title: 'Входной контроль и экспертизы ИПК — ОВСС',
    shortTitle: 'ВК и экспертизы — ОВСС',
    icon: 'ClipboardCheck',
    status: 'pilot',
    aiReadiness: 'medium',
    aiPotential: 'ВК >80%; экспертиза 50–80%',
    group: 'core',
    phase: 1,
    phaseLabel: 'Этап 1 — Q2 2026',
    description:
      'Входной контроль комплектности и корректности пакетов документов и профильная экспертиза заявок ИПК в части программ ОВСС: чек-листы по типу ОВСС и порогу стоимости, автоматическая экспертиза Excel-программы (~30 колонок, сверка с SAP), LLM-поддержка, интеграция с КАСУД. Отдельный проект от пилота по ремонтам.',
    goal:
      'Для заявок ОВСС: минуты на входной контроль, 1–2 дня на рутинную часть экспертизы вместо многодневного цикла; снижение ошибок и ручного труда.',
    scope: [
      'Чек-листы комплектности: тип ОВСС × порог стоимости (в т.ч. ОВСС-аналог, ОВСС-улучшенный)',
      'Автоматическая экспертиза программы ОВСС (Excel): формулы, сверка с SAP',
      'Профильная экспертиза ДИАК ЗФ и LLM-анализ заключений в контуре ОВСС',
      'Параллельная экспертиза с ДНПА; при необходимости — согласование с ДИТ ГО по смешанным кейсам',
      'Проверка дат по производственному календарю РФ',
      'Интеграция с КАСУД (SOAP, статусы, уведомления)',
    ],
    stakeholders: [
      'ДИАК ЗФ (владелец процесса, экспертиза)',
      'ДМТО ЗФ',
      'ДНПА',
      'Секретарь ИПК (Руцков А.М.)',
      'ПО ГенИИ',
      'ДИТ (инфраструктура)',
    ],
    kpis: [
      { label: 'Потенциал ИИ (ВК)', value: '>80', unit: '%' },
      { label: 'Потенциал ИИ (экспертиза)', value: '50–80', unit: '%' },
      { label: 'Целевое время входного контроля', value: '0', unit: 'часов' },
      { label: 'Целевой срок экспертизы', value: '1–2', unit: 'дня' },
      { label: 'Версия системы', value: 'v2', unit: '' },
      { label: 'Интеграция', value: 'КАСУД + SAP', unit: '' },
    ],
    risks: [
      {
        title: 'Нестабильность интеграции с КАСУД',
        level: 'medium',
        mitigation: 'Mock-режим, cron-поллер статусов',
      },
      {
        title: 'Качество LLM по ОВСС-программам',
        level: 'medium',
        mitigation: 'Keyword-fallback, Langfuse, выборочный аудит',
      },
      {
        title: 'Сопротивление изменениям',
        level: 'high',
        mitigation: 'Обучение, вовлечение стейкхолдеров',
      },
    ],
    monthlyPlan: [
      {
        id: 'mp-ovss-1',
        title: 'Финализация пилота ОВСС (все комбинации чек-листов)',
        start: '2026-04-01',
        end: '2026-04-14',
        status: 'planned',
        description: 'Тестирование 8 комбинаций, устранение замечаний',
      },
      {
        id: 'mp-ovss-3',
        title: 'Уточнение ТЗ и передача задач ИИ-агентам (общий контур ИПК)',
        start: '2026-04-07',
        end: '2026-04-30',
        status: 'planned',
        description: 'С ПО ГенИИ: детализация, инструменты',
      },
      {
        id: 'mp-ovss-4',
        title: 'Расчёт ресурсов и мощностей',
        start: '2026-04-14',
        end: '2026-04-30',
        status: 'planned',
        dependencies: ['mp-ovss-3'],
        description: 'ДИАК ЗФ, ПО ГенИИ, ИБ, ДИТ, подрядчики',
      },
      {
        id: 'mp-ovss-5',
        title: 'Мобилизация команды масштабирования',
        start: '2026-05-01',
        end: '2026-05-15',
        status: 'planned',
        dependencies: ['mp-ovss-4'],
        description: 'Kick-off с ПО ГенИИ и ДИТ',
      },
      {
        id: 'mp-ovss-6',
        title: 'Методологически унифицированные процессы (ОВСС + стык с ремонтами)',
        start: '2026-05-01',
        end: '2026-05-31',
        status: 'planned',
        dependencies: ['mp-ovss-3'],
        description: 'Регламенты с учётом ИИ, РИРУИП',
      },
      {
        id: 'mp-ovss-7',
        title: 'Проектирование ЦРМ',
        start: '2026-05-15',
        end: '2026-05-31',
        status: 'planned',
        dependencies: ['mp-ovss-5'],
        description: 'Единое окно агентов, интеграции',
      },
      {
        id: 'mp-ovss-8',
        title: 'Промышленная эксплуатация — ОВСС',
        start: '2026-06-01',
        end: '2026-06-30',
        status: 'planned',
        dependencies: ['mp-ovss-6', 'mp-ovss-7'],
        description: 'Старт эффектов по заявкам ОВСС',
      },
      {
        id: 'mp-ovss-9',
        title: 'Подготовка к масштабированию на КС, ИТ, прочее (Этап 2)',
        start: '2026-06-15',
        end: '2026-06-30',
        status: 'planned',
        dependencies: ['mp-ovss-8'],
        description: 'Стадии КС, чек-листы ИТ и прочих типов',
      },
    ],
    tags: ['ОВСС', 'ИПК', 'ИИ-агенты', 'КАСУД', 'SAP', 'Экспертиза', 'Пилот'],
    repoUrl: 'https://github.com/Pavel-Liubitskii/ipk-navigator',
    startDate: '2026-01-01',
    targetDate: '2026-06-30',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 1b. ВХОДНОЙ КОНТРОЛЬ И ЭКСПЕРТИЗЫ ИПК — РЕМОНТЫ (пилот Этапа 1)
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'ipk-repairs',
    title: 'Входной контроль и экспертизы ИПК — ремонты',
    shortTitle: 'ВК и экспертизы — ремонты',
    icon: 'Package',
    status: 'pilot',
    aiReadiness: 'medium',
    aiPotential: 'ВК >80%; экспертиза 50–80%',
    group: 'core',
    phase: 1,
    phaseLabel: 'Этап 1 — Q2 2026',
    description:
      'Тот же продуктовый контур (ВК + экспертиза ИПК), но в отдельном проекте для капитализируемых ремонтов: адаптированные чек-листы, семантика строк, согласование с методологией ОВСС без смешения портфелей на этапе пилота.',
    goal:
      'Обеспечить для ремонтных заявок те же целевые сроки ВК и экспертизы, что и для ОВСС, с учётом специфики смет и ТКП по ремонтам.',
    scope: [
      'Чек-листы и сценарии входного контроля для капитализируемых ремонтов',
      'LLM-извлечение цен из ТКП и смет в домене ремонтов',
      'Профильная экспертиза ДИАК ЗФ по ремонтным пакетам',
      'Интеграция с КАСУД и согласование статусов с общим контуром ИПК',
      'Синхронизация методологии с проектом ОВСС (единые принципы, раздельные правила)',
    ],
    stakeholders: [
      'ДИАК ЗФ',
      'ДМТО ЗФ',
      'ДНПА',
      'Секретарь ИПК (Руцков А.М.)',
      'ПО ГенИИ',
      'ДИТ (инфраструктура)',
    ],
    kpis: [
      { label: 'Потенциал ИИ (ВК)', value: '>80', unit: '%' },
      { label: 'Потенциал ИИ (экспертиза)', value: '50–80', unit: '%' },
      { label: 'Целевое время входного контроля', value: '0', unit: 'часов' },
      { label: 'Целевой срок экспертизы', value: '1–2', unit: 'дня' },
    ],
    risks: [
      {
        title: 'Расхождение методологии с ОВСС',
        level: 'medium',
        mitigation: 'Общие регламенты Этапа 1, регулярная синхронизация команд',
      },
      {
        title: 'Качество извлечения данных из смет/ТКП',
        level: 'medium',
        mitigation: 'Шаблоны, обучение промптов, ручная выборка',
      },
    ],
    monthlyPlan: [
      {
        id: 'mp-rep-1',
        title: 'Пилот ремонтов (после стабилизации чек-листов ОВСС)',
        start: '2026-04-07',
        end: '2026-04-21',
        status: 'planned',
        description: 'Адаптация чек-листов, семантические паспорта строк',
      },
      {
        id: 'mp-rep-2',
        title: 'Промышленная эксплуатация — ремонты',
        start: '2026-06-01',
        end: '2026-06-30',
        status: 'planned',
        dependencies: ['mp-rep-1'],
        description: 'Параллельно с выводом ОВСС в промышленную эксплуатацию',
      },
    ],
    tags: ['Ремонты', 'ИПК', 'КАСУД', 'SAP', 'Экспертиза', 'Пилот'],
    repoUrl: 'https://github.com/Pavel-Liubitskii/ipk-navigator',
    startDate: '2026-01-01',
    targetDate: '2026-06-30',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // ДЕТАЛИЗАЦИЯ ЗАДАЧ «ЦИФРОВОЙ ДИАК ЗФ» (подготовительный проект Этапа 1)
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'digital-diak-task-detailing',
    title: 'Детализация задач «Цифровой ДИАК ЗФ»',
    shortTitle: 'Детализация Цифрового ДИАК ЗФ',
    icon: 'LineChart',
    status: 'active',
    aiReadiness: 'medium',
    aiPotential: '50–80%',
    group: 'core',
    phase: 1,
    phaseLabel: 'Этап 1 — Q2 2026',
    description:
      'Подготовительный проект первого этапа программы: уточнение технического задания «Цифровой ДИАК ЗФ», декомпозиция направлений деятельности департамента на конкретные задачи и пакеты работ. Определяются границы автоматизации, перечень задач, пригодных для передачи ИИ-агентам, потребность в дополнительных инструментах и инфраструктуре, а также в кадровых и вычислительных ресурсах. Результат — мобилизованная команда и единая основа для масштабирования последующих инициатив цифровизации.',
    goal:
      'Сформировать согласованное ТЗ программы, реестр задач с оценкой ИИ-потенциала и автоматизации, план ресурсов и мобилизации команды, обеспечивающий переход к реализации пилотов и масштабированию без методологических разрывов.',
    scope: [
      'Декомпозиция направлений деятельности департамента на процессы и задачи (WBS-логика)',
      'Описание текущих процессов (AS-IS): шаги, роли, используемые системы и формируемые результаты',
      'Выявление узких мест, ручных операций и неэффективностей в текущих процессах',
      'Уточнение и согласование ТЗ программы «Цифровой ДИАК ЗФ» с ключевыми стейкхолдерами на основе анализа текущих процессов',
      'Определение границ автоматизации и зон ручного контроля (что должно оставаться под контролем человека)',
      'Оценка применимости ИИ-агентов по задачам с учётом ограничений (данные, регуляторика, качество)',
      'Идентификация потребности в дополнительных инструментах (платформы, интеграции, лицензии)',
      'Оценка потребности в ресурсах и мощностях (FTE, компетенции, вычислительные ресурсы)',
      'Формирование плана мобилизации команды и распределения ролей на этап реализации',
      'Подготовка входных данных для приоритизации портфеля инициатив и формирования дорожной карты Этапа 2',
    ],
    stakeholders: [
      // Порядок в сиде → toStakeholder: R, A, C, I, I, I. Несколько «C» в UI выставьте вручную (ДИТ, ПО ГенИИ).
      // Состав сознательно НЕ копирует ВК ОВСС / бюджет: там другой предмет (операции ИПК vs ПРП/SAP).
      'Проектный офис / координация программы «Цифровой ДИАК ЗФ»',
      'Руководство ДИАК ЗФ',
      'ИБ ЗФ',
      'ДИТ ЗФ (данные и интеграции)',
      'ПО ГенИИ',
      'Секретарь ИПК / контур КАСУД (стык с регламентом ИПК)',
    ],
    kpis: [
      { label: 'Готовность уточнённого ТЗ', value: '100', unit: '%' },
      { label: 'Задач в реестре с оценкой ИИ', value: '≥40', unit: '' },
      { label: 'Согласованный план ресурсов', value: '1', unit: 'версия' },
    ],
    risks: [
      {
        title: 'Расширение scope без фиксации границ этапа',
        level: 'medium',
        mitigation: 'Жёсткие критерии завершения Этапа 1 и отдельный перечень «бэклог на Этап 2»',
      },
      {
        title: 'Недоступность ключевых экспертов для оценки задач',
        level: 'medium',
        mitigation: 'Календарные слоты и приоритет со стороны руководства департамента',
      },
      {
        title: 'Завышенные ожидания по автоматизации на основе первых оценок',
        level: 'low',
        mitigation: 'Явное разделение «оценка потенциала» и «обязательства по пилотам»',
      },
    ],
    tags: ['Цифровой ДИАК', 'Этап 1', 'ТЗ', 'Дорожная карта', 'Ресурсы'],
    startDate: '2026-04-01',
    targetDate: '2026-06-30',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 3. ПЛАНИРОВАНИЕ И КОНТРОЛЬ БЮДЖЕТА — ОВСС (пилот «бюджетного» контура)
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'budget-ovss',
    title: 'Планирование и контроль бюджета — ОВСС',
    shortTitle: 'Бюджет — ОВСС',
    icon: 'BarChart3',
    status: 'planned',
    aiReadiness: 'high',
    aiPotential: '>80%',
    group: 'core',
    phase: 2,
    phaseLabel: 'Этап 2 — Q2–Q4 2026',
    description:
      'Цифровой инструмент планирования и контроля инвестиционного бюджета по программам ОВСС: формирование сводных программ приобретения оборудования, мониторинг исполнения «здесь и сейчас», автоматическая сверка с SAP ERP и быстрая аналитика для руководства.',
    goal:
      'Автоматизировать планирование и контроль исполнения бюджета по портфелю ОВСС, обеспечить прозрачность данных в реальном времени и сократить ручной труд при формировании отчётности.',
    scope: [
      'Формирование консолидированного ПРП по портфелю ОВСС в рамках годового и квартального планирования',
      'Автоматическая сверка планов и факта с SAP ERP по объектам ОВСС',
      'Мониторинг исполнения в режиме реального времени',
      'Автоматические алерты при отклонениях от утверждённых лимитов и плановых заданий',
      'Формирование аналитических отчётов для руководства ЗФ и ФЦО',
    ],
    stakeholders: [
      'ДИАК ЗФ (методология)',
      'ФЦО — куратор номенклатуры ОВСС',
      'Проектные офисы',
      'ДИТ (интеграция SAP)',
    ],
    kpis: [
      { label: 'Срок формирования квартального отчёта', value: 'с 5 до 1', unit: 'дней' },
      { label: 'Доля автоматически сверяемых с SAP позиций', value: 'до 100', unit: '%' },
      { label: 'Целевой эффект EBITDA (программа в целом)', value: '~5', unit: 'млрд руб.' },
    ],
    risks: [
      {
        title: 'Зависимость от данных SAP ERP',
        level: 'high',
        mitigation: 'Ранняя интеграция с ДИТ, тестовый контур SAP',
      },
    ],
    tags: ['Бюджет', 'ОВСС', 'SAP', 'ИИ-агенты', 'Этап 2'],
    startDate: '2026-04-01',
    targetDate: '2026-06-30',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 3a. ПЛАНИРОВАНИЕ И КОНТРОЛЬ БЮДЖЕТА — РЕМОНТЫ/ПРОЧЕЕ
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'budget-repairs',
    title: 'Планирование и контроль бюджета — Ремонты/прочее',
    shortTitle: 'Бюджет — Ремонты',
    icon: 'BarChart3',
    status: 'planned',
    aiReadiness: 'high',
    aiPotential: '>80%',
    group: 'core',
    phase: 2,
    phaseLabel: 'Этап 2 — Q2–Q4 2026',
    description:
      'Цифровой инструмент планирования и контроля бюджета по программам капитализируемых ремонтов (ККР, КРР, КРЛО): прозрачность планов и факта, автоматическая сверка с SAP, снижение риска отклонений от утверждённых лимитов.',
    goal:
      'Автоматизировать планирование, мониторинг и контроль исполнения инвестиционного бюджета по ремонтному портфелю с автоматической сверкой с SAP ERP.',
    scope: [
      'Формирование консолидированного ПРП по ремонтному портфелю',
      'Автоматическая сверка планов и факта с SAP ERP по объектам ремонтов',
      'Мониторинг исполнения программ ККР, КРР и КРЛО в режиме реального времени',
      'Автоматические алерты при отклонениях от утверждённых лимитов и плановых заданий',
      'Формирование аналитических отчётов по ремонтному портфелю для руководства ЗФ и ФЦО',
    ],
    stakeholders: [
      'ДИАК ЗФ (методология)',
      'ФЦО — куратор номенклатуры капитализируемых ремонтов',
      'Проектные офисы',
      'ДИТ (интеграция SAP)',
    ],
    kpis: [
      { label: 'Срок формирования квартального отчёта', value: 'с 5 до 1', unit: 'дней' },
      { label: 'Доля автоматически сверяемых с SAP позиций', value: 'до 100', unit: '%' },
    ],
    risks: [
      {
        title: 'Зависимость от данных SAP ERP по объектам ремонтов',
        level: 'high',
        mitigation: 'Ранняя интеграция с ДИТ, тестовый контур SAP',
      },
    ],
    tags: ['Бюджет', 'Ремонты', 'ККР', 'КРР', 'КРЛО', 'SAP', 'Этап 2'],
    startDate: '2026-07-01',
    targetDate: '2026-09-30',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 3b. ПЛАНИРОВАНИЕ И КОНТРОЛЬ БЮДЖЕТА — КС и ИТ
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'budget-ks-it',
    title: 'Планирование и контроль бюджета — КС и ИТ',
    shortTitle: 'Бюджет — КС и ИТ',
    icon: 'BarChart3',
    status: 'planned',
    aiReadiness: 'high',
    aiPotential: '>80%',
    group: 'core',
    phase: 3,
    phaseLabel: 'Этап 3 — Q4 2026 – Q2 2027',
    description:
      'Цифровой инструмент планирования и контроля бюджета по программам капитального строительства (ПКС) и ИТ-инициативам: единый контур для двух портфелей, контроль освоения и оплаты по объектам и ИТ-проектам, автоматическая сверка с SAP ERP.',
    goal:
      'Автоматизировать планирование и контроль бюджета по портфелям КС и ИТ, обеспечить консолидированную отчётность и автоматическую сверку с SAP ERP без ручного труда.',
    scope: [
      'Формирование консолидированных ПРП по проектам КС ЗФ',
      'Формирование ПРП по ИТ-программе Компании (Портфель ИТ-проектов)',
      'Автоматическая сверка планов и факта с SAP ERP по объектам КС и ИТ',
      'Мониторинг освоения и оплаты по проектам в режиме реального времени',
      'Автоматические алерты при отклонениях от лимитов финансирования',
      'Формирование аналитических отчётов для руководства ЗФ, ФЦО и ДИТ ГО',
    ],
    stakeholders: [
      'ДИАК ЗФ (методология)',
      'ФЦО — куратор номенклатуры КС',
      'ДИТ ГО (ИТ-портфель)',
      'Проектные офисы (КС)',
      'ДИТ (интеграция SAP)',
    ],
    kpis: [
      { label: 'Срок формирования квартального отчёта', value: 'с 5 до 1', unit: 'дней' },
      { label: 'Доля автоматически сверяемых с SAP позиций', value: 'до 100', unit: '%' },
    ],
    risks: [
      {
        title: 'Зависимость от данных SAP ERP и АСУ ПБ по объектам КС',
        level: 'high',
        mitigation: 'Ранняя интеграция с ДИТ, тестовый контур SAP, согласование с ДИТ ГО',
      },
    ],
    tags: ['Бюджет', 'КС', 'ИТ', 'SAP', 'Этап 3'],
    startDate: '2026-10-01',
    targetDate: '2026-12-31',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 4. НАПИСАНИЕ И ВЫПУСК ПРОТОКОЛОВ ИПК
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'ipk-protocols',
    title: 'Написание и выпуск протоколов ИПК',
    shortTitle: 'Протоколы ИПК',
    icon: 'FileText',
    status: 'planned',
    aiReadiness: 'high',
    aiPotential: '>80%',
    group: 'core',
    phase: 2,
    phaseLabel: 'Этап 2 — Q4 2026',
    description:
      'Автоматическое формирование протоколов заседаний ИПК на основе результатов экспертиз и согласований. Исключение ручного труда секретаря ИПК.',
    goal:
      'Генерация проекта протокола ИПК за минуты вместо нескольких рабочих дней.',
    scope: [
      'Автоматическая агрегация результатов всех экспертиз',
      'LLM-генерация текста протокола по шаблону',
      'Интеграция с КАСУД для публикации',
      'Электронное согласование и подписание',
    ],
    stakeholders: [
      'Секретарь ИПК (Руцков А.М.)',
      'ДИАК ЗФ',
      'Члены ИПК',
      'ПО ГенИИ',
    ],
    kpis: [
      { label: 'Потенциал ИИ', value: '>80', unit: '%' },
      { label: 'Целевое время формирования протокола', value: '<30', unit: 'мин.' },
    ],
    risks: [
      {
        title: 'Юридические требования к формату протокола',
        level: 'medium',
        mitigation: 'Согласование шаблона с юристами на этапе проектирования',
      },
    ],
    tags: ['Протоколы', 'LLM', 'КАСУД', 'Секретарь ИПК'],
    startDate: '2026-10-01',
    targetDate: '2026-12-31',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // ВК И ЭКСПЕРТИЗЫ ИПК — КС (Этап 2, Q4 2026)
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'ipk-ks',
    title: 'Входной контроль и экспертизы ИПК — капитальное строительство (КС)',
    shortTitle: 'ВК и экспертизы — КС',
    icon: 'ClipboardCheck',
    status: 'planned',
    aiReadiness: 'medium',
    aiPotential: 'ВК >80%; экспертиза 50–80%',
    group: 'core',
    phase: 2,
    phaseLabel: 'Этап 2 — Q4 2026',
    description:
      'Масштабирование контура ВК и экспертизы на заявки капитального строительства: стадии проекта, отраслевые чек-листы, согласование с проектными офисами и КАСУД/SAP после пилотов ОВСС и ремонтов.',
    goal:
      'Целевые сроки как на пилоте: минуты на ВК, 1–2 дня на рутинную экспертизу для портфеля КС.',
    scope: [
      'Чек-листы входного контроля по стадиям и типам объектов КС',
      'Экспертиза пакетов КС в пайплайне ИПК, LLM-поддержка доменных документов',
      'Интеграция с КАСУД и SAP в контуре КС (Q4 2026)',
      'Методическая преемственность с пилотами Этапа 1',
    ],
    stakeholders: [
      'ДИАК ЗФ',
      'Проектные офисы (КС)',
      'ДМТО ЗФ',
      'ДНПА',
      'Секретарь ИПК (Руцков А.М.)',
      'ПО ГенИИ',
      'ДИТ',
    ],
    kpis: [
      { label: 'Потенциал ИИ (ВК)', value: '>80', unit: '%' },
      { label: 'Потенциал ИИ (экспертиза)', value: '50–80', unit: '%' },
      { label: 'Целевое время ВК', value: '0', unit: 'часов' },
      { label: 'Целевой срок экспертизы', value: '1–2', unit: 'дня' },
    ],
    risks: [
      {
        title: 'Высокая вариативность типов заявок КС',
        level: 'high',
        mitigation: 'Поэтапное подключение типов, приоритизация с ПО',
      },
      {
        title: 'Нагрузка на интеграции',
        level: 'medium',
        mitigation: 'Нагрузочные тесты, мониторинг очередей',
      },
    ],
    tags: ['ИПК', 'КС', 'Капстрой', 'Экспертиза', 'Этап 2'],
    repoUrl: 'https://github.com/Pavel-Liubitskii/ipk-navigator',
    startDate: '2026-10-01',
    targetDate: '2026-12-31',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // ВК И ЭКСПЕРТИЗЫ ИПК — ИТ (Этап 2, Q4 2026)
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'ipk-it',
    title: 'Входной контроль и экспертизы ИПК — ИТ',
    shortTitle: 'ВК и экспертизы — ИТ',
    icon: 'Search',
    status: 'planned',
    aiReadiness: 'medium',
    aiPotential: '50–80%',
    group: 'core',
    phase: 2,
    phaseLabel: 'Этап 2 — Q4 2026',
    description:
      'Отдельный проект для ИТ-закупок и ИТ-составляющих инвестиционных инициатив: ВК комплектности, экспертиза с обязательным участием ДИТ ГО, LLM-анализ ТКП и технических приложений.',
    goal:
      'Сократить цикл ВК и рутинной экспертизы по ИТ-направлению до целевых значений пилота при соблюдении ИБ и архитектурных норм.',
    scope: [
      'Чек-листы для ИТ-заявок и смешанных инициатив (ИТ + прочее)',
      'Параллельная экспертиза с ДИТ ГО',
      'LLM-поддержка извлечения и сопоставления условий в ИТ-документации',
      'Интеграция с КАСУД и маршрутизация согласований',
    ],
    stakeholders: [
      'ДИАК ЗФ',
      'ДИТ ГО',
      'ДИТ (инфраструктура, ИБ)',
      'ДМТО ЗФ',
      'ПО ГенИИ',
      'Секретарь ИПК (Руцков А.М.)',
    ],
    kpis: [
      { label: 'Потенциал ИИ', value: '50–80', unit: '%' },
      { label: 'Целевое время ВК', value: '0', unit: 'часов' },
      { label: 'Целевой срок экспертизы', value: '1–2', unit: 'дня' },
    ],
    risks: [
      {
        title: 'ИБ и ограничения на обработку данных',
        level: 'high',
        mitigation: 'Согласование контуров, обезличивание, on-prem LLM при необходимости',
      },
      {
        title: 'Сложность ТКП и лицензионных моделей',
        level: 'medium',
        mitigation: 'Шаблоны, глоссарии, экспертный разбор выборки',
      },
    ],
    tags: ['ИПК', 'ИТ', 'Экспертиза', 'Этап 2'],
    repoUrl: 'https://github.com/Pavel-Liubitskii/ipk-navigator',
    startDate: '2026-10-01',
    targetDate: '2026-12-31',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // ВК И ЭКСПЕРТИЗЫ ИПК — ПРОЧЕЕ (Этап 2, Q4 2026)
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'ipk-other',
    title: 'Входной контроль и экспертизы ИПК — прочие инициативы',
    shortTitle: 'ВК и экспертизы — прочее',
    icon: 'FolderOpen',
    status: 'planned',
    aiReadiness: 'needs_analysis',
    aiPotential: 'Требует проработки',
    group: 'core',
    phase: 2,
    phaseLabel: 'Этап 2 — Q4 2026',
    description:
      'Инвестиционные инициативы вне явных корзин ОВСС, ремонтов, КС и ИТ (например, отдельные виды ОРЭП, специальные программы): единый каркас ВК и экспертизы с настраиваемыми чек-листами и правилами маршрутизации.',
    goal:
      'Не оставлять «белые пятна» в автоматизации ИПК: по мере зрелости методологии подключать прочие типы заявок с теми же принципами прослеживаемости.',
    scope: [
      'Реестр типов заявок «прочее» и матрица применимости чек-листов',
      'Поэтапное подключение к контуру КАСУД/SAP',
      'LLM и экспертиза — по мере готовности доменных шаблонов',
      'Согласование границ с смежными портфелями (ОВСС, КС, ИТ)',
    ],
    stakeholders: [
      'ДИАК ЗФ',
      'ДМТО ЗФ',
      'ДНПА',
      'Проектные офисы',
      'ПО ГенИИ',
      'Секретарь ИПК (Руцков А.М.)',
    ],
    kpis: [
      { label: 'Охват типов заявок', value: 'По плану Q4', unit: '' },
      { label: 'Потенциал ИИ', value: 'По оценке', unit: '' },
    ],
    risks: [
      {
        title: 'Размытие scope «прочего»',
        level: 'medium',
        mitigation: 'Жёсткий реестр типов, пересмотр границ ежеквартально',
      },
    ],
    tags: ['ИПК', 'Прочее', 'Экспертиза', 'Этап 2'],
    repoUrl: 'https://github.com/Pavel-Liubitskii/ipk-navigator',
    startDate: '2026-10-01',
    targetDate: '2026-12-31',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 5. ПРОЕКТНЫЙ КОНТРОЛЬ — ОВСС (пилот «контрольного» контура)
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'project-control-ovss',
    title: 'Проектный контроль — ОВСС',
    shortTitle: 'Проектный контроль — ОВСС',
    icon: 'Package',
    status: 'planned',
    aiReadiness: 'high',
    aiPotential: '>80%',
    group: 'core',
    phase: 2,
    phaseLabel: 'Этап 2 — Q2–Q4 2026',
    description:
      'Цифровой контур контроля заказов на поставку оборудования по программам ОВСС: статусы в SAP «здесь и сейчас», раннее выявление рисков срыва сроков и автоматизация согласования договоров в зоне ответственности ДИАК ЗФ.',
    goal:
      'Автоматизировать мониторинг исполнения заказов и согласование договоров по портфелю ОВСС, минимизировать ручной труд и снизить риск срыва сроков.',
    scope: [
      'Мониторинг статусов заказов в SAP ERP (ME23N, MB51) по портфелю ОВСС',
      'Автоматические алерты при отклонениях от плановых сроков поставки',
      'Согласование договоров по ОВСС в зоне ответственности ДИАК ЗФ',
      'Интеграция с КАСУД для документооборота',
      'Сводные отчёты по статусам исполнения программ ОВСС',
    ],
    stakeholders: [
      'ДИАК ЗФ',
      'ДМТО ЗФ',
      'Поставщики',
      'Проектные офисы',
      'ДИТ (SAP)',
    ],
    kpis: [
      { label: 'Целевой срок согласования договора', value: 'с 14 до 2', unit: 'дней' },
      { label: 'Доля автоматически отслеживаемых заказов', value: 'до 100', unit: '%' },
    ],
    risks: [
      {
        title: 'Качество данных в SAP ERP',
        level: 'high',
        mitigation: 'Аудит данных SAP перед интеграцией',
      },
    ],
    tags: ['Заказы', 'ОВСС', 'SAP', 'Поставки', 'Пилот'],
    startDate: '2026-04-01',
    targetDate: '2026-06-30',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 5a. ПРОЕКТНЫЙ КОНТРОЛЬ — РЕМОНТЫ
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'project-control-repairs',
    title: 'Проектный контроль — Ремонты',
    shortTitle: 'Проектный контроль — Ремонты',
    icon: 'Package',
    status: 'planned',
    aiReadiness: 'high',
    aiPotential: '>80%',
    group: 'core',
    phase: 2,
    phaseLabel: 'Этап 2 — Q2–Q4 2026',
    description:
      'Цифровой контур контроля заказов на поставку оборудования, материалов и услуг по программам капитализируемых ремонтов (ККР, КРР, КРЛО): мониторинг статусов в SAP, раннее выявление рисков срыва сроков, автоматизация согласования договоров.',
    goal:
      'Автоматизировать мониторинг исполнения заказов и согласование договоров по ремонтному портфелю, минимизировать ручной труд и снизить риск срыва сроков поставки и пусконаладки.',
    scope: [
      'Мониторинг статусов заказов в SAP ERP (ME23N, MB51) по ремонтному портфелю',
      'Автоматические алерты при отклонениях от плановых сроков поставки',
      'Согласование договоров по капитализируемым ремонтам в зоне ответственности ДИАК ЗФ',
      'Интеграция с КАСУД для документооборота по ремонтным заявкам',
      'Сводные отчёты по статусам исполнения программ ККР, КРР, КРЛО',
    ],
    stakeholders: [
      'ДИАК ЗФ',
      'ДМТО ЗФ',
      'Поставщики',
      'Проектные офисы (ремонтные)',
      'ДИТ (SAP)',
    ],
    kpis: [
      { label: 'Целевой срок согласования договора', value: 'с 14 до 2', unit: 'дней' },
      { label: 'Доля автоматически отслеживаемых заказов', value: 'до 100', unit: '%' },
    ],
    risks: [
      {
        title: 'Качество данных в SAP ERP по ремонтным позициям',
        level: 'high',
        mitigation: 'Аудит данных SAP перед интеграцией, выборочная сверка с дефектными ведомостями',
      },
    ],
    tags: ['Заказы', 'Ремонты', 'SAP', 'Поставки', 'Этап 2'],
    startDate: '2026-07-01',
    targetDate: '2026-09-30',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 5b. ПРОЕКТНЫЙ КОНТРОЛЬ — КС и ИТ
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'project-control-ks-it',
    title: 'Проектный контроль — КС и ИТ',
    shortTitle: 'Проектный контроль — КС и ИТ',
    icon: 'Package',
    status: 'planned',
    aiReadiness: 'high',
    aiPotential: '>80%',
    group: 'core',
    phase: 3,
    phaseLabel: 'Этап 3 — Q4 2026 – Q2 2027',
    description:
      'Цифровой контур проектного контроля заказов и согласования договоров по программам капитального строительства и ИТ-инициативам: единое окно по статусам ПИР/СМР/ПНР и ИТ-поставок, ранние алерты при отклонениях, автоматизация согласования договоров.',
    goal:
      'Автоматизировать мониторинг исполнения заказов и согласование договоров по портфелям КС и ИТ, обеспечить раннее выявление рисков срыва сроков и сократить цикл согласования договоров.',
    scope: [
      'Мониторинг статусов заказов в SAP ERP по объектам КС (ПИР, СМР, ПНР) и ИТ-поставкам',
      'Автоматические алерты при отклонениях от КСГ и плановых сроков поставки',
      'Согласование договоров по проектам КС и ИТ-проектам в зоне ответственности ДИАК ЗФ',
      'Интеграция с КАСУД для документооборота по заявкам КС и ИТ',
      'Сводные отчёты по исполнению ПКС и ИТ-программы',
    ],
    stakeholders: [
      'ДИАК ЗФ',
      'ДМТО ЗФ',
      'ДИТ ГО (ИТ-портфель)',
      'Проектные офисы (КС)',
      'ДИТ (SAP)',
    ],
    kpis: [
      { label: 'Целевой срок согласования договора', value: 'с 14 до 2', unit: 'дней' },
      { label: 'Доля автоматически отслеживаемых заказов', value: 'до 100', unit: '%' },
    ],
    risks: [
      {
        title: 'Качество данных в SAP ERP по объектам КС и ИТ',
        level: 'high',
        mitigation: 'Аудит данных SAP перед интеграцией, согласование контуров с ДИТ ГО',
      },
    ],
    tags: ['Заказы', 'КС', 'ИТ', 'SAP', 'Этап 3'],
    startDate: '2027-01-01',
    targetDate: '2027-03-31',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 6. СОГЛАСОВАНИЕ ДОГОВОРОВ
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'contracts',
    title: 'Согласование договоров в зоне ответственности',
    shortTitle: 'Договоры',
    icon: 'FileSignature',
    status: 'planned',
    aiReadiness: 'high',
    aiPotential: '>80%',
    group: 'support',
    phase: 3,
    phaseLabel: 'Этап 3 — поддерживающие процессы',
    description:
      'Автоматизация процесса согласования договоров, находящихся в зоне ответственности ДИАК ЗФ. Проверка соответствия условий договора утверждённым параметрам ИПК.',
    goal:
      'Сократить цикл согласования договора с нескольких недель до 1–2 дней.',
    scope: [
      'Автоматическая проверка условий договора vs. параметры ИПК',
      'LLM-анализ текста договора',
      'Маршрутизация на согласование через КАСУД',
      'Трекинг статусов согласования',
    ],
    stakeholders: [
      'ДИАК ЗФ',
      'Юридический департамент',
      'ДМТО ЗФ',
      'ПО ГенИИ',
    ],
    kpis: [
      { label: 'Потенциал ИИ', value: '>80', unit: '%' },
      { label: 'Целевой срок согласования', value: '1–2', unit: 'дня' },
    ],
    risks: [
      {
        title: 'Юридические риски при LLM-анализе договоров',
        level: 'high',
        mitigation: 'Обязательная верификация юристом финального варианта',
      },
    ],
    tags: ['Договоры', 'LLM', 'КАСУД', 'Согласование', 'Этап 3', 'Непрофильные'],
    startDate: '2026-04-01',
    targetDate: '2026-09-30',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 7. ЭКСПЕРТИЗА ТЭО
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'teo-expertise',
    title: 'Экспертиза ТЭО',
    shortTitle: 'Экспертиза ТЭО',
    icon: 'Calculator',
    status: 'planned',
    aiReadiness: 'medium',
    aiPotential: '50–80%',
    group: 'core',
    phase: 3,
    phaseLabel: 'Этап 3 — Q2 2027',
    description:
      'Экспертиза технико-экономических обоснований инвестиционных проектов. Автоматизация проверки финансовых моделей и расчётов.',
    goal:
      'Автоматизировать проверку финансовых расчётов ТЭО, снизить риск ошибок при экспертизе.',
    scope: [
      'Проверка финансовых моделей (NPV, IRR, срок окупаемости)',
      'LLM-анализ текстовой части ТЭО',
      'Сверка данных с SAP ERP и рыночными бенчмарками',
      'Формирование заключения по шаблону',
    ],
    stakeholders: [
      'ДИАК ЗФ (УОМИД)',
      'Проектные офисы',
      'ФЦО',
      'ПО ГенИИ',
    ],
    kpis: [
      { label: 'Потенциал ИИ', value: '50–80', unit: '%' },
      { label: 'Целевой срок экспертизы ТЭО', value: '2–3', unit: 'дня' },
    ],
    risks: [
      {
        title: 'Сложность финансовых моделей',
        level: 'medium',
        mitigation: 'Поэтапное внедрение: сначала стандартные шаблоны ТЭО',
      },
    ],
    tags: ['ТЭО', 'Финансы', 'LLM', 'Этап 3'],
    startDate: '2027-04-01',
    targetDate: '2027-06-30',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 8. ПОСТИНВЕСТИЦИОННЫЙ КОНТРОЛЬ
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'post-invest',
    title: 'Постинвестиционный контроль',
    shortTitle: 'Постинвест',
    icon: 'TrendingUp',
    status: 'planned',
    aiReadiness: 'high',
    aiPotential: '>80%',
    group: 'core',
    phase: 3,
    phaseLabel: 'Этап 3 — Q2 2027',
    description:
      'Мониторинг достижения плановых показателей реализованных инвестиционных проектов. Автоматический сбор данных из SAP и формирование отчётов.',
    goal:
      'Обеспечить непрерывный мониторинг постинвестиционных показателей без ручного сбора данных.',
    scope: [
      'Автоматический сбор факта из SAP ERP',
      'Сравнение план/факт по ключевым показателям проекта',
      'Формирование постинвестиционных отчётов',
      'Алерты при существенных отклонениях',
    ],
    stakeholders: [
      'ДИАК ЗФ (УМИД)',
      'Проектные офисы',
      'ФЦО',
      'Руководство ЗФ',
    ],
    kpis: [
      { label: 'Потенциал ИИ', value: '>80', unit: '%' },
      { label: 'Периодичность мониторинга', value: 'Ежемесячно', unit: '' },
    ],
    risks: [
      {
        title: 'Неполнота данных в SAP после завершения проекта',
        level: 'medium',
        mitigation: 'Регламент ввода данных по завершении проекта',
      },
    ],
    tags: ['Постинвест', 'SAP', 'Мониторинг', 'Этап 3'],
    startDate: '2027-04-01',
    targetDate: '2027-06-30',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 9. ПОДГОТОВКА АНАЛИТИЧЕСКИХ ОТЧЁТОВ
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'analytics',
    title: 'Подготовка аналитических отчётов',
    shortTitle: 'Аналитика',
    icon: 'LineChart',
    status: 'planned',
    aiReadiness: 'high',
    aiPotential: '>80%',
    group: 'core',
    phase: 2,
    phaseLabel: 'Этап 2 — Q4 2026',
    description:
      'Автоматизация подготовки аналитических отчётов и взаимодействия с подразделениями. Полная прозрачность данных «здесь и сейчас».',
    goal:
      'Формировать любой аналитический отчёт за минуты, обеспечить доступ к данным в нужном разрезе.',
    scope: [
      'Автоматическая агрегация данных из SAP, КАСУД, внутренних систем',
      'LLM-генерация нарративной части отчётов',
      'Интерактивные дашборды для руководства',
      'Взаимодействие с подразделениями через ИИ-агентов',
    ],
    stakeholders: [
      'ДИАК ЗФ',
      'Руководство ЗФ',
      'Смежные подразделения',
      'ПО ГенИИ',
    ],
    kpis: [
      { label: 'Потенциал ИИ', value: '>80', unit: '%' },
      { label: 'Целевое время подготовки отчёта', value: '<5', unit: 'мин.' },
    ],
    risks: [
      {
        title: 'Качество и актуальность исходных данных',
        level: 'medium',
        mitigation: 'Data governance, регламент актуализации данных',
      },
    ],
    tags: ['Аналитика', 'LLM', 'Дашборды', 'Отчётность'],
    startDate: '2026-10-01',
    targetDate: '2026-12-31',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 10. УПРАВЛЕНИЕ ДОКУМЕНТАЦИЕЙ КИСМ
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'kism',
    title: 'Управление документацией КИСМ и аудиты',
    shortTitle: 'КИСМ',
    icon: 'FolderOpen',
    status: 'planned',
    aiReadiness: 'needs_analysis',
    aiPotential: 'Требует проработки',
    group: 'support',
    phase: 3,
    phaseLabel: 'Этап 3 — Q2 2027',
    description:
      'Автоматизация управления документацией корпоративной информационной системы менеджмента и проведения аудитов.',
    goal:
      'Снизить трудозатраты на ведение документации КИСМ и подготовку к аудитам.',
    scope: [
      'Автоматическое обновление документации КИСМ',
      'Подготовка к аудитам (чек-листы, сбор доказательств)',
      'Интеграция с КАСУД',
    ],
    stakeholders: ['ДИАК ЗФ', 'Служба качества', 'ИБ'],
    kpis: [
      { label: 'Потенциал ИИ', value: 'Требует проработки', unit: '' },
    ],
    risks: [
      {
        title: 'Требования регуляторов к документации',
        level: 'medium',
        mitigation: 'Согласование с юристами и службой качества',
      },
    ],
    tags: ['КИСМ', 'Аудит', 'Документооборот', 'Этап 3'],
    startDate: '2027-04-01',
    targetDate: '2027-06-30',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 11. КАДРОВЫЙ ДОКУМЕНТООБОРОТ
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'hr-docs',
    title: 'Кадровый документооборот',
    shortTitle: 'Кадры',
    icon: 'Users',
    status: 'planned',
    aiReadiness: 'high',
    aiPotential: '>80%',
    group: 'support',
    phase: 3,
    phaseLabel: 'Этап 3 — Q2 2027',
    description:
      'Автоматизация кадрового документооборота: приказы, заявления, справки, табели. Непрофильная задача, высокий потенциал автоматизации.',
    goal:
      'Полная автоматизация рутинного кадрового документооборота.',
    scope: [
      'Генерация типовых кадровых документов',
      'Маршрутизация на подписание',
      'Интеграция с 1С:ЗУП',
    ],
    stakeholders: ['ДИАК ЗФ', 'HR-служба ЗФ', 'ДИТ'],
    kpis: [
      { label: 'Потенциал ИИ', value: '>80', unit: '%' },
    ],
    risks: [
      {
        title: 'Требования трудового законодательства',
        level: 'low',
        mitigation: 'Согласование с HR и юристами',
      },
    ],
    tags: ['HR', 'Документооборот', 'Этап 3'],
    startDate: '2027-04-01',
    targetDate: '2027-06-30',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 12. АДМИНИСТРАТИВНО-ХОЗЯЙСТВЕННЫЕ ВОПРОСЫ
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'admin',
    title: 'Административно-хозяйственные вопросы',
    shortTitle: 'АХО',
    icon: 'Building2',
    status: 'planned',
    aiReadiness: 'needs_analysis',
    aiPotential: 'Требует проработки',
    group: 'support',
    phase: 3,
    phaseLabel: 'Этап 3 — Q2 2027',
    description:
      'Автоматизация административно-хозяйственных процессов: заявки на командировки, материально-техническое обеспечение, управление офисом.',
    goal:
      'Снизить административную нагрузку на сотрудников ДИАК ЗФ.',
    scope: [
      'Автоматизация заявок на командировки',
      'Управление МТО',
      'Административные запросы через ИИ-агента',
    ],
    stakeholders: ['ДИАК ЗФ', 'АХО ЗФ', 'HR-служба'],
    kpis: [
      { label: 'Потенциал ИИ', value: 'Требует проработки', unit: '' },
    ],
    risks: [
      {
        title: 'Низкий приоритет относительно профильных задач',
        level: 'low',
        mitigation: 'Реализация в последнюю очередь (Этап 3)',
      },
    ],
    tags: ['АХО', 'Административные', 'Этап 3'],
    startDate: '2027-04-01',
    targetDate: '2027-06-30',
  },
];

// ─── ROADMAP SUMMARY ──────────────────────────────────────────────────────
export const ROADMAP_PHASES = [
  {
    id: 1,
    label: 'Этап 1',
    period: 'Q2 2026',
    title: 'Завершение пилотов и подготовка к масштабированию',
    color: '#00A651',
    projects: ['ipk-ovss', 'ipk-repairs', 'digital-diak-task-detailing'],
  },
  {
    id: 2,
    label: 'Этап 2',
    period: 'Q2–Q4 2026',
    title: 'Ключевые процессы: ОВСС и Ремонты',
    color: '#0055A5',
    projects: [
      'budget-ovss',
      'budget-repairs',
      'project-control-ovss',
      'project-control-repairs',
      'ipk-ks',
      'ipk-it',
      'ipk-other',
      'ipk-protocols',
      'analytics',
    ],
  },
  {
    id: 3,
    label: 'Этап 3',
    period: 'Q4 2026 – Q2 2027',
    title: 'Ключевые процессы: КС и ИТ, поддерживающие процессы',
    color: '#4A90D9',
    projects: [
      'budget-ks-it',
      'project-control-ks-it',
      'post-invest',
      'teo-expertise',
      'kism',
      'hr-docs',
      'admin',
    ],
  },
];

export const AMBITION = {
  headline: 'Стать первым цифровым департаментом среди промышленных компаний в РФ',
  effects: [
    { value: '~5 млрд руб.', label: 'рост EBITDA', icon: 'TrendingUp' },
    { value: '>80%', label: 'задач выполняют ИИ-агенты', icon: 'Bot' },
    { value: '0 часов', label: 'на контроли и экспертизы', icon: 'Clock' },
  ],
};
